/**
 * Withdrawal-strategy pure helpers — Phase 6 Session 3 (the FIRE
 * sophistication layer).
 *
 * Five strategies + one dispatcher. Trinity / Monte Carlo helpers in
 * `stress-test.ts` plumb the dispatcher into their per-year withdrawal
 * step (replacing the literal `balance × (1+r) - annual_spend_cents`
 * subtraction).
 *
 *   - `bengen_constant` — constant inflation-adjusted withdrawal
 *     (Bengen 1994 original; the 6.1/6.2 default).
 *   - `guyton_klinger` — guardrail rules: cut spending after a market
 *     drop that pushes the current WR > floor + threshold; raise when
 *     WR < ceiling - threshold (Guyton & Klinger 2006).
 *   - `vpw` — fixed age-by-age % of current portfolio, derived from
 *     the Bogleheads 2020 annuity formula in `data/vpw-tables.ts`.
 *   - `cape_based` — Bernstein "Ages of the Investor" (2012) rule:
 *     spend_rate_pct = max(cape_floor, 1.75 + 50/CAPE).
 *   - `dynamic_band` — flexes prev_spend ±band_pct based on portfolio
 *     drift vs initial target (Kitces dynamic-spending hybrid).
 *
 * **Cents everywhere.** All amounts integer cents. Helpers return a
 * number; the caller rounds at write-time.
 *
 * Mirrored verbatim at web/src/lib/util/withdrawal-strategies.ts per
 * the 5.1 lock (web does not import from @hearth/shared).
 */

import {
  VPW_TABLE_BOGLEHEADS_2020,
  vpwWithdrawalRatePct,
  type VpwTableParams,
} from "../data/vpw-tables.js";

// ============================================================================
// Public types
// ============================================================================

export type WithdrawalStrategy =
  | "bengen_constant"
  | "guyton_klinger"
  | "vpw"
  | "cape_based"
  | "dynamic_band";

/**
 * Strategy-config payload. Sparse — only the active strategy's knobs
 * need populating; the dispatcher reads what it needs and ignores the
 * rest.
 */
export interface WithdrawalStrategyConfig {
  readonly strategy: WithdrawalStrategy;
  // Guyton-Klinger
  readonly gk_floor_pct?: number;
  readonly gk_ceiling_pct?: number;
  readonly gk_cut_threshold_pct?: number;
  readonly gk_raise_threshold_pct?: number;
  // VPW
  readonly vpw_table?: VpwTableParams;
  // CAPE (Bernstein)
  readonly cape_floor_pct?: number;
  // Dynamic band
  readonly dynamic_band_pct?: number;
}

/**
 * Per-year state passed to `applyWithdrawalStrategy`. Each field is what
 * the strategy logic needs from the simulation loop — the helper
 * doesn't peek into the loop's internals.
 */
export interface WithdrawalState {
  /** Year offset, 0 = first withdrawal year. */
  readonly year_offset: number;
  /** The simulation's initial inflation-adjusted target spend in cents.
   *  Bengen + Guyton-Klinger + dynamic_band reference this; VPW and
   *  CAPE-based ignore it. */
  readonly initial_spend_cents: number;
  /** Last year's actual spend in cents. */
  readonly prev_spend_cents: number;
  /** Portfolio balance at the START of this year, in cents (after the
   *  prior year's return + withdrawal). */
  readonly current_balance_cents: number;
  /** The household's headline target withdrawal rate %. Used by
   *  Guyton-Klinger to compute floor/ceiling. */
  readonly target_withdrawal_rate_pct: number;
  /** Caller's age at this year_offset (only required for VPW). */
  readonly age?: number;
  /** Asset allocation (only required for VPW; defaults to 60/40). */
  readonly equity_pct?: number;
  readonly bond_pct?: number;
  /** Shiller CAPE/PE10 at this year (only required for cape_based). */
  readonly cape_value?: number;
}

// ============================================================================
// Defaults — canonical values for each strategy (used when caller doesn't
// override a knob). All public so tests + UI can read them.
// ============================================================================

/** Guyton-Klinger canonical defaults: 80% floor, 120% ceiling, ±20%
 *  trigger. Source: Guyton & Klinger, "Decision Rules and Maximum
 *  Initial Withdrawal Rates" (2006, Journal of Financial Planning). */
export const GK_DEFAULTS = {
  floor_pct: 80,
  ceiling_pct: 120,
  cut_threshold_pct: 20,
  raise_threshold_pct: 20,
  /** Spend reduction applied when a cut trigger fires. -10% canonical. */
  cut_pct: 10,
  /** Spend increase applied when a raise trigger fires. +10% canonical. */
  raise_pct: 10,
} as const;

/** Bernstein CAPE rule (1.75% + 50/CAPE) — defaults the floor at 2%
 *  to prevent vanishingly-small withdrawals at peak CAPE values. */
export const CAPE_DEFAULTS = {
  base_pct: 1.75,
  cape_coefficient: 50,
  floor_pct: 2,
} as const;

/** Dynamic-band default ±10% of initial spend. */
export const DYNAMIC_BAND_DEFAULTS = {
  band_pct: 10,
} as const;

// ============================================================================
// Per-strategy helpers
// ============================================================================

/** Bengen constant inflation-adjusted withdrawal. Pure passthrough — the
 *  Trinity dataset's real-return formulation already removes the
 *  inflation walk, so "constant in real terms" is the same number every
 *  year. */
export function bengenConstant(state: WithdrawalState): number {
  return state.initial_spend_cents;
}

/**
 * Guyton-Klinger guardrails. The classic capital preservation rule:
 *
 *   - current_wr = prev_spend / current_balance
 *   - upper trigger: current_wr > target × (1 + cut_threshold/100)
 *     → cut prev_spend by `cut_pct` (10%), but never below
 *       initial_spend × floor_pct/100.
 *   - lower trigger: current_wr < target × (1 - raise_threshold/100)
 *     → raise prev_spend by `raise_pct` (10%), but never above
 *       initial_spend × ceiling_pct/100.
 *   - else: prev_spend unchanged.
 *
 * Returns this year's spend in cents.
 */
export function guytonKlinger(
  state: WithdrawalState,
  config: WithdrawalStrategyConfig,
): number {
  const floorPct = config.gk_floor_pct ?? GK_DEFAULTS.floor_pct;
  const ceilingPct = config.gk_ceiling_pct ?? GK_DEFAULTS.ceiling_pct;
  const cutThresholdPct =
    config.gk_cut_threshold_pct ?? GK_DEFAULTS.cut_threshold_pct;
  const raiseThresholdPct =
    config.gk_raise_threshold_pct ?? GK_DEFAULTS.raise_threshold_pct;

  const floorCents = (state.initial_spend_cents * floorPct) / 100;
  const ceilingCents = (state.initial_spend_cents * ceilingPct) / 100;

  // Year 0 (first year of retirement) — no prior to react to. Use
  // initial_spend.
  if (state.year_offset === 0 || state.current_balance_cents <= 0) {
    return state.initial_spend_cents;
  }

  const currentWrPct =
    (state.prev_spend_cents / state.current_balance_cents) * 100;
  const upperTrigger =
    state.target_withdrawal_rate_pct * (1 + cutThresholdPct / 100);
  const lowerTrigger =
    state.target_withdrawal_rate_pct * (1 - raiseThresholdPct / 100);

  let next = state.prev_spend_cents;
  if (currentWrPct > upperTrigger) {
    next = state.prev_spend_cents * (1 - GK_DEFAULTS.cut_pct / 100);
  } else if (currentWrPct < lowerTrigger) {
    next = state.prev_spend_cents * (1 + GK_DEFAULTS.raise_pct / 100);
  }
  return Math.max(floorCents, Math.min(ceilingCents, next));
}

/**
 * VPW (Variable Percentage Withdrawal). Spend = current_balance ×
 * vpw_rate(age) / 100 where the rate is generated by the annuity
 * formula in `data/vpw-tables.ts`.
 *
 * **Always solvent**: VPW spends a strict % of current balance, so the
 * portfolio never depletes — but spending swings widely with the market
 * (the famous "feast and famine" tradeoff vs Bengen).
 */
export function vpw(
  state: WithdrawalState,
  config: WithdrawalStrategyConfig,
): number {
  if (state.current_balance_cents <= 0) return 0;
  const params = config.vpw_table ?? VPW_TABLE_BOGLEHEADS_2020;
  const age = state.age ?? 65;
  const eqPct = state.equity_pct ?? 60;
  const bdPct = state.bond_pct ?? 40;
  const ratePct = vpwWithdrawalRatePct({
    age,
    equity_pct: eqPct,
    bond_pct: bdPct,
    params,
  });
  return (state.current_balance_cents * ratePct) / 100;
}

/**
 * CAPE-based (Bernstein "Ages of the Investor", 2012):
 *
 *   spend_rate_pct = max(cape_floor_pct, base_pct + cape_coefficient/CAPE)
 *   spend_cents    = current_balance × spend_rate_pct / 100
 *
 * High CAPE → low spend (markets expensive, conservative withdrawal).
 * Low CAPE  → high spend (markets cheap, aggressive withdrawal).
 *
 * When `cape_value` is missing or non-positive, falls back to Bengen
 * constant (degrades gracefully when CAPE history doesn't cover a
 * Monte Carlo's stochastic future year).
 */
export function capeBased(
  state: WithdrawalState,
  config: WithdrawalStrategyConfig,
): number {
  const cape = state.cape_value;
  if (cape === undefined || cape <= 0) {
    return state.initial_spend_cents;
  }
  const floorPct = config.cape_floor_pct ?? CAPE_DEFAULTS.floor_pct;
  const rawRatePct =
    CAPE_DEFAULTS.base_pct + CAPE_DEFAULTS.cape_coefficient / cape;
  const spendRatePct = Math.max(floorPct, rawRatePct);
  return (state.current_balance_cents * spendRatePct) / 100;
}

/**
 * Dynamic band (Kitces hybrid). Flexes prev_spend within ±band_pct of
 * initial_spend based on portfolio drift vs the on-track balance. The
 * "on-track" reference at year_offset Y is initial_balance × (1+r)^Y
 * with r ≈ target_withdrawal_rate (a rough proxy for "if the portfolio
 * is keeping pace with constant withdrawal").
 *
 * Simplification chosen for v1: when current_balance > prev_spend × (100/wr) × 1.05,
 * raise spend by 5%; when current_balance < prev_spend × (100/wr) × 0.95,
 * cut by 5%. Bounded by band_pct from initial_spend on both sides.
 *
 * Phase 11 may layer in more sophisticated reference paths.
 */
export function dynamicBand(
  state: WithdrawalState,
  config: WithdrawalStrategyConfig,
): number {
  const bandPct = config.dynamic_band_pct ?? DYNAMIC_BAND_DEFAULTS.band_pct;
  if (state.year_offset === 0) return state.initial_spend_cents;

  const lowerBoundCents =
    (state.initial_spend_cents * (100 - bandPct)) / 100;
  const upperBoundCents =
    (state.initial_spend_cents * (100 + bandPct)) / 100;

  // Reference ratio: prev_spend / current_balance vs target_wr.
  if (state.current_balance_cents <= 0) return lowerBoundCents;
  const currentWrPct =
    (state.prev_spend_cents / state.current_balance_cents) * 100;

  let next = state.prev_spend_cents;
  if (currentWrPct < state.target_withdrawal_rate_pct * 0.95) {
    next = state.prev_spend_cents * 1.05; // portfolio outpacing → spend more
  } else if (currentWrPct > state.target_withdrawal_rate_pct * 1.05) {
    next = state.prev_spend_cents * 0.95; // portfolio falling behind → cut
  }
  return Math.max(lowerBoundCents, Math.min(upperBoundCents, next));
}

// ============================================================================
// Dispatcher
// ============================================================================

/**
 * Single entry point — Trinity + Monte Carlo + the comparison helper
 * call this. Defaults to Bengen-constant when config is undefined,
 * preserving 6.1/6.2 backward compatibility.
 */
export function applyWithdrawalStrategy(
  state: WithdrawalState,
  config?: WithdrawalStrategyConfig,
): number {
  const strategy: WithdrawalStrategy = config?.strategy ?? "bengen_constant";
  switch (strategy) {
    case "bengen_constant":
      return bengenConstant(state);
    case "guyton_klinger":
      return guytonKlinger(state, config!);
    case "vpw":
      return vpw(state, config!);
    case "cape_based":
      return capeBased(state, config!);
    case "dynamic_band":
      return dynamicBand(state, config!);
  }
}
